import React, { useState, useEffect } from 'react';
import { Table, Select, Alert, Space, Typography, Divider } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;

// All supported field types with descriptions
const FIELD_TYPES = [
  { value: 'TextInput', label: 'Text Input', description: 'Single line text' },
  { value: 'TextArea', label: 'Text Area', description: 'Multi-line text' },
  { value: 'RichText', label: 'Rich Text', description: 'Formatted text editor' },
  { value: 'Number', label: 'Number', description: 'Numeric input' },
  { value: 'Email', label: 'Email', description: 'Email address' },
  { value: 'Phone', label: 'Phone', description: 'Phone number' },
  { value: 'URL', label: 'URL', description: 'Website link' },
  { value: 'Date', label: 'Date', description: 'Date picker' },
  { value: 'DateTime', label: 'Date & Time', description: 'Date and time picker' },
  { value: 'Time', label: 'Time', description: 'Time picker' },
  { value: 'Dropdown', label: 'Dropdown', description: 'Select from list' },
  { value: 'MultiSelect', label: 'Multi-Select', description: 'Select multiple items' },
  { value: 'RadioGroup', label: 'Radio Group', description: 'Single choice from options' },
  { value: 'CheckboxGroup', label: 'Checkbox Group', description: 'Multiple choices' },
  { value: 'Toggle', label: 'Toggle', description: 'Yes/No switch' },
  { value: 'Slider', label: 'Slider', description: 'Range selector' },
  { value: 'Rating', label: 'Rating', description: 'Star rating' },
  { value: 'FileUpload', label: 'File Upload', description: 'File attachment' },
];

/**
 * FieldTypeMapper - Review and adjust auto-detected field types
 * Used in import flow to let users confirm/change field types before template creation
 */
export default function FieldTypeMapper({ sheets, onChange, onValidate }) {
  const [mappedSheets, setMappedSheets] = useState(sheets);

  useEffect(() => {
    setMappedSheets(sheets);
  }, [sheets]);

  useEffect(() => {
    // Notify parent of changes
    if (onChange) {
      onChange(mappedSheets);
    }

    // Validate - all fields must have a type
    const allFieldsHaveType = mappedSheets.every(sheet =>
      sheet.sections.every(section =>
        section.categories.every(category =>
          category.questions.every(q => q.field_type && q.field_type !== '')
        )
      )
    );

    if (onValidate) {
      onValidate(allFieldsHaveType);
    }
  }, [mappedSheets, onChange, onValidate]);

  const handleFieldTypeChange = (sheetIdx, sectionIdx, categoryIdx, questionIdx, newType) => {
    const updated = [...mappedSheets];
    updated[sheetIdx].sections[sectionIdx].categories[categoryIdx].questions[questionIdx].field_type = newType;
    setMappedSheets(updated);
  };

  // Flatten structure for table display
  const getTableData = () => {
    const data = [];
    mappedSheets.forEach((sheet, sheetIdx) => {
      sheet.sections.forEach((section, sectionIdx) => {
        section.categories.forEach((category, categoryIdx) => {
          category.questions.forEach((question, questionIdx) => {
            data.push({
              key: `${sheetIdx}-${sectionIdx}-${categoryIdx}-${questionIdx}`,
              sheetName: sheet.name,
              sectionName: section.name,
              categoryName: category.name,
              questionText: question.question,
              fieldType: question.field_type || '',
              indices: { sheetIdx, sectionIdx, categoryIdx, questionIdx },
            });
          });
        });
      });
    });
    return data;
  };

  const columns = [
    {
      title: 'Sheet',
      dataIndex: 'sheetName',
      key: 'sheet',
      width: 150,
      ellipsis: true,
    },
    {
      title: 'Field Name',
      dataIndex: 'questionText',
      key: 'question',
      width: 250,
      ellipsis: true,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Detected Type',
      dataIndex: 'fieldType',
      key: 'detectedType',
      width: 180,
      render: (type) => {
        const fieldType = FIELD_TYPES.find(ft => ft.value === type);
        return (
          <Space>
            <Text type="secondary">{fieldType?.label || 'Not detected'}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Field Type',
      key: 'fieldType',
      width: 250,
      render: (_, record) => {
        const { sheetIdx, sectionIdx, categoryIdx, questionIdx } = record.indices;
        return (
          <Select
            value={record.fieldType || undefined}
            onChange={(value) => handleFieldTypeChange(sheetIdx, sectionIdx, categoryIdx, questionIdx, value)}
            style={{ width: '100%' }}
            placeholder="Select field type"
            status={!record.fieldType ? 'error' : ''}
            options={FIELD_TYPES.map(ft => ({
              label: ft.label,
              value: ft.value,
              title: ft.description,
            }))}
            showSearch
            optionFilterProp="label"
          />
        );
      },
    },
  ];

  const tableData = getTableData();
  const totalFields = tableData.length;
  const mappedFields = tableData.filter(d => d.fieldType).length;
  const unmappedFields = totalFields - mappedFields;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Alert
        message="Review and Adjust Field Types"
        description={
          <div>
            <p>We've auto-detected field types based on column names and data. Please review and adjust as needed.</p>
            <Text strong>Progress: </Text>
            <Text type={unmappedFields === 0 ? 'success' : 'warning'}>
              {mappedFields} of {totalFields} fields configured
            </Text>
            {unmappedFields > 0 && (
              <Text type="danger" style={{ marginLeft: 8 }}>
                ({unmappedFields} remaining)
              </Text>
            )}
          </div>
        }
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
      />

      <div>
        <Title level={5}>Field Mappings ({mappedSheets.length} sheet{mappedSheets.length > 1 ? 's' : ''})</Title>
        <Table
          columns={columns}
          dataSource={tableData}
          pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (total) => `${total} fields` }}
          scroll={{ x: 900 }}
          size="small"
        />
      </div>

      <Alert
        message="Field Types Reference"
        description={
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px', marginTop: '8px' }}>
            {FIELD_TYPES.slice(0, 8).map(ft => (
              <div key={ft.value}>
                <Text strong>{ft.label}:</Text> <Text type="secondary" style={{ fontSize: '12px' }}>{ft.description}</Text>
              </div>
            ))}
          </div>
        }
        type="info"
        showIcon={false}
        style={{ marginTop: '16px' }}
      />
    </Space>
  );
}
